import { hasPermission, PERMISSIONS, type Permission, systemRoleLabel } from '@repo/contracts';
import { type NavItem, AppShell as SharedAppShell } from '@repo/ui';
import { Activity, ListChecks, TriangleAlert } from 'lucide-react';
import { Outlet } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { activeAppRoleOf } from '@/hooks/auth/app-roles';
import { useSignOut } from '@/hooks/auth/use-sign-out';
import { useOpenConflictCount } from '@/hooks/sync/use-open-conflict-count';
import { useRefreshOnRunFinish } from '@/hooks/sync/use-refresh-on-run-finish';
import { SwitchRoleMenu } from '@/shared/components/switch-role/SwitchRoleMenu';
import {
  selectActiveRole,
  selectCurrentUser,
  selectHeldRoles,
  selectPermissions,
} from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

import { MobileNav } from './MobileNav';

const navItems = (conflictCount: number): NavItem[] => [
  {
    to: APP_ROUTES.home,
    label: 'Overview',
    icon: Activity,
    permission: PERMISSIONS.directoryRead,
    end: true,
  },
  {
    to: APP_ROUTES.runs,
    label: 'Runs',
    icon: ListChecks,
    permission: PERMISSIONS.directoryRead,
  },
  {
    to: APP_ROUTES.conflicts,
    label: 'Conflicts',
    icon: TriangleAlert,
    permission: PERMISSIONS.directoryRead,
    badge: conflictCount,
  },
];

export function AppShell() {
  const user = useAppSelector(selectCurrentUser);
  const effectivePermissions = useAppSelector(selectPermissions);
  const activeRole = useAppSelector(selectActiveRole);
  const heldRoles = useAppSelector(selectHeldRoles);
  const conflictCount = useOpenConflictCount();
  useRefreshOnRunFinish();
  const signOut = useSignOut();
  const subtitleRole = activeAppRoleOf(heldRoles, activeRole);

  const canAccess = (permission?: string): boolean =>
    !permission || hasPermission(effectivePermissions, [permission as Permission]);

  return (
    <SharedAppShell
      productName="Cyrino"
      sectionName="Jobs"
      navItems={navItems(conflictCount)}
      canAccess={canAccess}
      fullName={user?.fullName}
      subtitle={subtitleRole ? systemRoleLabel(subtitleRole) : undefined}
      onSignOut={signOut}
      accountMenuExtra={<SwitchRoleMenu />}
    >
      <MobileNav onSignOut={signOut} />
      <Outlet />
    </SharedAppShell>
  );
}
